import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Button, TextField, Typography, IconButton, CardContent
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { financialService } from '../utils/api';
import { useAppContext } from '../context/AppContext';
import { jwtDecode } from "jwt-decode";
import { APP_CONFIG } from '../appConfig';
import { getTrackingData } from "../utils/tracking";

const OTPInputCard = ({ phoneNumber, onVerify, onResend, onBack }) => {
  const { state, actions } = useAppContext();
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [timer, setTimer] = useState(120);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([...Array(6)].map(() => React.createRef()));
  const timerRef = useRef(null);
  const requestIdRef = useRef(0);
  const isVerifyingRef = useRef(false);

  useEffect(() => {
    startTimer();
    inputRefs.current[0].current.focus();
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    const code = otp.join('');
    if (code.length === 6) {
      const timeout = setTimeout(() => {
        handleVerify();
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [otp]);

  const startTimer = () => {
    setTimer(120);
    setCanResend(false);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const updated = [...otp];
    updated[i] = val;
    setOtp(updated);
    setError('');
    if (val && i < 5) inputRefs.current[i + 1].current.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) {
      inputRefs.current[i - 1].current.focus();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d{6}$/.test(paste)) {
      const arr = paste.split('');
      setOtp(arr);
      inputRefs.current[5].current.focus();
      setError('');
    }
    e.preventDefault();
  };

  const handleVerify = async () => {
    if (isVerifyingRef.current) return;

    const code = otp.join('');
    if (code.length < 6) {
      setError('Please enter all 6 digits');
      return;
    }

    isVerifyingRef.current = true;
    setIsVerifying(true);
    setError('');
    const currentRequestId = ++requestIdRef.current;

    try {
      const response = await financialService.verifyOTP({
        phone_number: phoneNumber,
        otp: code,
        full_name: state.clientName,
        email_id: state.email,
        ...(getTrackingData() || {}),
      });

      if (response.access_token) {
        // Store access token
        const decoded_token = jwtDecode(response.access_token);
        if (decoded_token.isAuthenticated){
          localStorage.setItem(APP_CONFIG.accessTokenStorageKey, response.access_token);
          actions.verifyOTP(response.access_token, phoneNumber);
          // Call parent success callback
          // onVerify?.(response);
        }else{
          setError(response.message || 'Invalid OTP. Please try again.');
        }
        
      }else {
        setError(response.message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error("Error while verifying OTP:", error);
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message;
      setError(backendMessage || 'Verification failed. Please try again.');
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsVerifying(false);
        isVerifyingRef.current = false;
      }
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    setOtp(new Array(6).fill(''));
    setError('');
    
    try {
      const response = await financialService.sendOTP({ "phone_number": phoneNumber });
      if (response.status_code === 200) {
        startTimer();
        inputRefs.current[0].current.focus();
        onResend?.();
      } else {
        setError('Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      console.error("Error while resending OTP:", error);
      setError('Failed to resend OTP. Please check your connection.');
    }
  };

  const handleBack = () => {
    // Clear timer when going back
    clearInterval(timerRef.current);
    onBack?.();
  };

  const maskedPhone = phoneNumber ? `+91 xxxxxx${phoneNumber.slice(-4)}` : '';

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <CardContent>
      <Box display="flex" alignItems="center" mb={2}>
        <IconButton onClick={handleBack} size="small" disabled={isVerifying}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight="bold" ml={1}>
          Verify Phone Number
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Enter the 6-digit OTP sent to {maskedPhone}
      </Typography>

      <Box display="flex" justifyContent="space-between" gap={1} mb={2}>
        {otp.map((val, i) => (
          <TextField
            key={i}
            type="tel"
            inputMode="numeric"
            inputRef={inputRefs.current[i]}
            value={val}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={i === 0 ? handlePaste : undefined}
            inputProps={{
              maxLength: 1,
              style: {
                textAlign: 'center',
                fontSize: '1.25rem',
                padding: '10px 0'
              }
            }}
            variant="outlined"
            sx={{ width: 48 }}
            disabled={isVerifying}
          />
        ))}
      </Box>

      {error && (
        <Typography color="error" variant="body2" align="center" mb={2}>
          {error}
        </Typography>
      )}

      <Typography variant="body2" align="center" mb={2} color="text.secondary">
        {timer > 0 ? (
          `Resend OTP in ${formatTime(timer)}`
        ) : (
          <Button 
            variant="text" 
            onClick={handleResend}
            disabled={!canResend}
            sx={{ textTransform: 'none' }}
          >
            Resend OTP
          </Button>
        )}
      </Typography>

      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={handleVerify}
        disabled={isVerifying || otp.join('').length < 6}
        sx={{ textTransform: 'none', fontWeight: 'bold' }}
      >
        {isVerifying ? 'Verifying...' : 'Verify & Continue'}
      </Button>
    </CardContent>
  );
};

export default OTPInputCard;


